import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';
import { DataTableDirective, DataTablesModule } from 'angular-datatables';
import { Config } from 'datatables.net';
import { Subject } from 'rxjs';
import { InvoiceArticleComponent } from '../../components/invoice-article/invoice-article.component';
import { ArticleInvoice } from '../../models/article-invoice';
import { Invoice } from '../../models/invoice';
import { AuthenticationService } from '../../services/auth/authentication.service';
import { InvoiceService } from '../../services/invoice.service';
import { ModalService } from '../../services/modal/modal.service';

@Component({
  selector: 'app-invoices',
  imports: [DataTablesModule, CommonModule, RouterModule],
  templateUrl: './invoices.component.html',
  styleUrl: './invoices.component.scss',
})
export class InvoicesComponent {
  dtOptions!: Config;

  invoices!: any[];

  fullyLoaded: boolean = false;

  @ViewChild(DataTableDirective)
  datatableElement!: DataTableDirective;
  dtTrigger: Subject<any> = new Subject<any>();

  constructor(
    private invoiceService: InvoiceService,
    private authService: AuthenticationService,
    private modalService: ModalService,
    private vcr: ViewContainerRef,
    private router: Router,
    private destroyRef: DestroyRef,
    private changeDetector: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.invoiceService
      .getInvoices()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp) => {
          this.invoices = resp;
          this.fullyLoaded = true;
          this.changeDetector.detectChanges();
          this.dtTrigger.next(null);
        },
        error: (err: HttpErrorResponse) => {
          if (err.status == 401) {
            this.authService.logout();
          } else {
            this.modalService.showErrorDialog(this.vcr, err.error.message);
          }
        },
      });

    this.dtOptions = {
      pagingType: 'full_numbers',
      destroy: true,
      search: {
        caseInsensitive: true,
        smart: true,
      },
      ajax: (dataTablesParameters: any, callback) => {
        callback({ data: this.invoices });
        this.datatableElement.dtInstance.then((api) => {
          api.off('click', 'td.dt-control');
          api.on('click', 'td.dt-control', (e: any) => {
            let tr = e.target.closest('tr');
            let row = api.row(tr);

            if (row.child.isShown()) {
              row.child.hide();
            } else {
              row.child(this.renderSubtable(row.data().articles)).show();
            }
          });
        });
      },
      lengthMenu: [5, 10, 20, 50],
      columns: [
        {
          className: 'dt-control',
          orderable: false,
          data: null,
          defaultContent: '',
        },
        {
          title: 'Partner',
          data: 'partner.name',
        },
        {
          title: 'Status',
          data: 'status.name',
        },
        {
          title: 'Invoice number',
          data: 'invoiceNumber',
        },
        {
          title: 'Date',
          data: 'date',
        },
        {
          title: 'Amount',
          data: 'amount',
        },
        {
          title: 'Payment status',
          data: 'paymentStatus',
          render: (data, type, row, meta) => {
            switch (data) {
              case 'Not paid':
              case 'Will not be paid':
                return `<td><i class="mdi mdi-circle text-danger"></i> ${data}</td>`;

              case 'Paid':
                return `<td><i class="mdi mdi-circle text-success"></i> ${data}</td>`;

              default:
                return `<td><i class="mdi mdi-circle text-warning"></i> ${data}</td>`;
            }
          },
        },
        {
          title: 'Actions',
          data: null,
          orderable: false,
          render: (data, type, row, meta) => {
            return `<a href="javascript: void(0);" id="edit" class="action-icon"><i class="mdi mdi-pencil"></i></a>
            <a href="javascript: void(0);" id="duplicate" class="action-icon"><i class="ri-file-copy-2-fill"></i></a>
            <a href="javascript: void(0);" id="print" class="action-icon"><i class="ri-printer-fill"></i></a>
            <a href="javascript: void(0);" id="pay" class="action-icon"><i class="ri-coins-fill"></i></a>
            <a href="javascript: void(0);" id="delete" class="action-icon"> <i class="mdi mdi-delete"></i></a>`;
          },
        },
      ],
      rowCallback: (row: Node, data: any[] | object, index: number) => {
        $('td:last-child #delete', row).off('click');
        $('td:last-child #edit', row).off('click');
        $('td:last-child #duplicate', row).off('click');
        $('td:last-child #pay', row).off('click');
        $('td:last-child #print', row).off('click');
        $('td:last-child #delete', row).on('click', () =>
          this.deleteInvoice((data as any).id)
        );
        $('td:last-child #edit', row).on('click', () =>
          this.editInvoice(data as Invoice)
        );
        $('td:last-child #duplicate', row).on('click', () =>
          this.duplicateInvoice(data as Invoice)
        );
        $('td:last-child #pay', row).on('click', () =>
          this.router.navigateByUrl(
            `dashboard/payments/${(data as Invoice).id}`
          )
        );
        $('td:last-child #print', row).on('click', () =>
          this.printInvoice(data as Invoice)
        );
      },
    };
  }

  printInvoice(invoice: Invoice): any {
    this.modalService
      .showOptionsDialog(this.vcr, 'Choose the language of report:', [
        { name: 'English', value: 'eng' },
        { name: 'Serbian', value: 'srb' },
      ])
      .getSelectedOption()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (lang) => {
          this.invoiceService
            .getInvoicePdf(invoice.id, lang)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (pdf) => {
                let blob: Blob = new Blob([pdf], { type: 'application/pdf' });
                let url = window.URL.createObjectURL(blob);

                let trigger = document.createElement('a');
                trigger.href = url;
                trigger.target = '_blank';
                trigger.download = 'invoice.pdf';
                document.body.appendChild(trigger);
                trigger.click();
                trigger.remove();
              },
              error: (err: HttpErrorResponse) => {
                if (err.status == 401) {
                  this.authService.logout();
                } else {
                  this.modalService.showErrorDialog(this.vcr, err.error.message);
                }
              },
            });
        },
      });
  }

  ngAfterViewInit(): void {
    this.dtTrigger.next(null);
  }

  renderSubtable(articles: ArticleInvoice[]) {
    let ref = this.vcr.createComponent(InvoiceArticleComponent);
    ref.instance.articles = articles;
    return ref.location.nativeElement;
  }

  deleteInvoice(id: number) {
    this.modalService
      .openYesNoDialog(this.vcr)
      .getAnswer()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (answer) => {
          if (answer) {
            this.invoiceService
              .deleteInvoice(id)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: () => {
                  this.invoices = this.invoices.filter(
                    (invoice: any) => invoice.id !== id
                  );
                  this.dtTrigger.next(null);
                },
                error: (err: HttpErrorResponse) => {
                  if (err.status == 401) {
                    this.authService.logout();
                  } else {
                    this.modalService.showErrorDialog(
                      this.vcr,
                      err.error.message
                    );
                  }
                },
              });
          }
        },
      });
  }

  editInvoice(invoice: Invoice) {
    localStorage.setItem('edit', JSON.stringify(invoice));
    this.router.navigateByUrl('dashboard/invoices/update');
  }

  duplicateInvoice(invoice: Invoice) {
    localStorage.setItem('duplicate', JSON.stringify(invoice));
    this.router.navigateByUrl('dashboard/invoices/duplicate');
  }
}
